import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, type User } from '../services/authService';
import { apiService } from '../services/api';
import sessionRefreshService from '../services/sessionRefreshService';
import { API_ENDPOINTS } from '../constants/api';
import { normalizeProfileSetupStatus, type ProfileSetupStatus } from '../utils/accountSetup';
import { applyResolvedDisplayTimeZone } from '../services/dateLocaleService';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithMicrosoftToken: (microsoftToken: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
  /** Employee account setup gate — null until loaded. */
  profileSetupStatus: ProfileSetupStatus | null;
  refreshProfileSetupStatus: () => Promise<void>;
  /** Outstanding tasks count for current user (sidebar badge, dashboard banner). Fetched once when user is set. */
  outstandingTasksCount: number;
  /** Update count after e.g. My Tasks page fetch so sidebar/dashboard stay in sync without a duplicate request. */
  updateOutstandingTasksCount: (count: number) => void;
  /** Open review disputes (Administrator only). Sidebar badge + My Dashboard alert. */
  openDisputesCount: number;
  updateOpenDisputesCount: (count: number) => void;
  /** Resolved IANA zone for on-screen timestamps (organisation default or Profile preference). */
  displayTimeZoneId: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [outstandingTasksCount, setOutstandingTasksCount] = useState(0);
  const [openDisputesCount, setOpenDisputesCount] = useState(0);
  const [profileSetupStatus, setProfileSetupStatus] = useState<ProfileSetupStatus | null>(null);
  const [displayTimeZoneId, setDisplayTimeZoneId] = useState('UTC');

  const resolveDisplayTimeZone = useCallback(async (preferred?: string | null) => {
    const resolved = await applyResolvedDisplayTimeZone(preferred);
    setDisplayTimeZoneId(resolved);
    return resolved;
  }, []);

  const refreshProfileSetupStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setProfileSetupStatus(null);
      return;
    }
    try {
      const res = await apiService.get<ProfileSetupStatus>(API_ENDPOINTS.USERS.PROFILE_SETUP_STATUS);
      setProfileSetupStatus(normalizeProfileSetupStatus(res.data));
    } catch {
      setProfileSetupStatus({ isExempt: false, isRestricted: false, missingItems: [] });
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          sessionRefreshService.start();
          await resolveDisplayTimeZone(userData.preferredIanaTimeZoneId);
          await refreshProfileSetupStatus();
        }
      } catch (error) {
        // 404/401/403 when no session or invalid token are expected; don't log as error
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } };
          const status = axiosError.response?.status;
          if (status === 401 || status === 403 || status === 404) {
            sessionRefreshService.stop();
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            setUser(null);
            setLoading(false);
            return;
          }
        }
        console.error('Auth initialization failed:', error);
        // Clear tokens on other auth-related failures
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { status?: number } };
          const status = axiosError.response?.status;
        if (status === 401 || status === 403) {
          sessionRefreshService.stop();
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
        }
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [refreshProfileSetupStatus, resolveDisplayTimeZone]);

  const preferredTimeZone = user?.preferredIanaTimeZoneId;
  const signedInUserId = user?.id;

  useEffect(() => {
    if (!signedInUserId) {
      return;
    }
    void resolveDisplayTimeZone(preferredTimeZone);
  }, [signedInUserId, preferredTimeZone, resolveDisplayTimeZone]);

  // Single fetch for outstanding tasks count when user is set (used by Sidebar badge and My Dashboard banner)
  useEffect(() => {
    if (!user) {
      setOutstandingTasksCount(0);
      setProfileSetupStatus(null);
      return;
    }
    const fetchOutstandingCount = async () => {
      try {
        const res = await apiService.get<{ totalCount: number }>(API_ENDPOINTS.USERS.OUTSTANDING_TASKS);
        setOutstandingTasksCount(res.data?.totalCount ?? 0);
      } catch {
        setOutstandingTasksCount(0);
      }
    };
    fetchOutstandingCount();
  }, [user]);

  // Administrators: open dispute count for sidebar badge and My Dashboard (lightweight endpoint)
  useEffect(() => {
    if (!user?.roles?.includes('Administrator')) {
      setOpenDisputesCount(0);
      return;
    }
    const fetchDisputeCount = async () => {
      try {
        const res = await apiService.get<{ count: number }>(API_ENDPOINTS.REVIEWS.DISPUTED_COUNT);
        setOpenDisputesCount(res.data?.count ?? 0);
      } catch {
        setOpenDisputesCount(0);
      }
    };
    fetchDisputeCount();
  }, [user]);

  const updateOutstandingTasksCount = useCallback((count: number) => {
    setOutstandingTasksCount(count);
  }, []);

  const updateOpenDisputesCount = useCallback((count: number) => {
    setOpenDisputesCount(count);
  }, []);

  const login = async (username: string, password: string) => {
    const response = await authService.login(username, password);
    localStorage.setItem('authToken', response.token);
    if (response.refreshToken) {
      localStorage.setItem('refreshToken', response.refreshToken);
    }
    await resolveDisplayTimeZone(response.user.preferredIanaTimeZoneId);
    setUser(response.user);
    sessionRefreshService.start();
    await refreshProfileSetupStatus();
  };

  const loginWithMicrosoftToken = async (microsoftToken: string) => {
    interface LoginResponse {
      token: string;
      refreshToken?: string;
      expiresAt?: string;
      user: User;
    }

    try {
      const response = await apiService.post<LoginResponse>(
        '/auth/microsoft-token',
        { token: microsoftToken }
      );

      if (response.data?.token) {
        localStorage.setItem('authToken', response.data.token);
        if (response.data.refreshToken) {
          localStorage.setItem('refreshToken', response.data.refreshToken);
        }
        await resolveDisplayTimeZone(response.data.user.preferredIanaTimeZoneId);
        setUser(response.data.user);
        sessionRefreshService.start();
        await refreshProfileSetupStatus();
      }
    } catch (error) {
      console.error('Microsoft token login failed:', error);
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        await resolveDisplayTimeZone(userData.preferredIanaTimeZoneId);
        await refreshProfileSetupStatus();
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // Don't clear tokens on refresh failure - might be a temporary network issue
    }
  };

  const logout = () => {
    sessionRefreshService.stop();
    // Clear tokens and user state immediately for responsive logout
    const refreshToken = localStorage.getItem('refreshToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setOutstandingTasksCount(0);
    setOpenDisputesCount(0);
    setProfileSetupStatus(null);

    // Try to revoke refresh token and logout on server (fire-and-forget)
    // These are best-effort calls - failures are expected if tokens are expired
    // and don't affect the logout flow
    if (refreshToken) {
      authService.revokeRefreshToken(refreshToken).catch(() => {
        // Silently ignore all errors - these are expected if tokens are expired
      });
    }
    authService.logout().catch(() => {
      // Silently ignore all errors - these are expected if tokens are expired
    });
  };

  const value = {
    user,
    login,
    loginWithMicrosoftToken,
    logout,
    refreshUser,
    loading,
    profileSetupStatus,
    refreshProfileSetupStatus,
    outstandingTasksCount,
    updateOutstandingTasksCount,
    openDisputesCount,
    updateOpenDisputesCount,
    displayTimeZoneId,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
